/**
 * Customer signature on the technician job screen (tracker item 42, phase 3).
 * Optional and never blocks Finish. Two ways to use it:
 * - before Finish: the pad and the printed name are read by the page when
 *   Finish is confirmed (`collect`) and travel with the finish request;
 * - on a completed job: "Save signature" posts to /jobs/:id/signature.
 * The current signature (image, signer name, time) is shown when there is one.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ITechnicianApi } from '../api';
import * as types from '../api/types';
import { SignaturePad, SignaturePadHandle } from '../components/SignaturePad';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { formatSignedAt, signatureErrorMessage, signerNameError } from '../utils/technician';

export type CollectedSignature =
  | { ok: true; signature: types.TechnicianSignatureInput | null }
  | { ok: false; message: string };

export interface TechnicianSignatureHandle {
  /** The drawn signature for Finish; `signature: null` when nothing was drawn. */
  collect(): Promise<CollectedSignature>;
  /** After Finish: clears the pad and shows the stored signature. */
  reload(): void;
}

export interface TechnicianSignatureSectionProps {
  api: Pick<ITechnicianApi, 'listTechnicianSignatures' | 'downloadTechnicianSignatureImage' | 'uploadTechnicianSignature'>;
  jobId: string;
  /** The job is COMPLETED / INVOICED: the pad saves straight away. */
  done: boolean;
  /** The job is CANCELLED: signatures are refused. */
  cancelled: boolean;
  /** Another request of the page is in flight. */
  busy: boolean;
  /** Company IANA timezone for the signed-at time (matches the tech note stamps). */
  timeZone?: string;
  /** The pad went from empty to inked and back (for the Finish confirmation). */
  onInkChange?: (hasInk: boolean) => void;
}

const box = 'rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200';

export const TechnicianSignatureSection = forwardRef<TechnicianSignatureHandle, TechnicianSignatureSectionProps>(function TechnicianSignatureSection(
  { api, jobId, done, cancelled, busy, timeZone, onInkChange },
  ref,
) {
  const padRef = useRef<SignaturePadHandle>(null);
  const [list, setList] = useState<types.JobSignatureList | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageAttempt, setImageAttempt] = useState(0);
  const [signerName, setSignerName] = useState('');
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const loadSeq = useRef(0);
  const saveInFlight = useRef(false);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await api.listTechnicianSignatures(jobId);
      if (seq === loadSeq.current) setList(result);
    } catch (err) {
      if (seq === loadSeq.current) setLoadError(getErrorMessage(err, 'Could not load the saved signature.'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [api, jobId]);

  useEffect(() => {
    setList(null);
    void load();
    return () => { loadSeq.current += 1; };
  }, [load]);

  // The current signature's image (Bearer-authenticated, so fetched as a Blob).
  const currentId = list?.current?.id ?? null;
  useEffect(() => {
    setImageUrl(null);
    setImageFailed(false);
    if (!currentId) return undefined;
    let cancelledLoad = false;
    let url: string | null = null;
    api.downloadTechnicianSignatureImage(jobId, currentId).then((blob) => {
      if (cancelledLoad) return;
      try {
        url = URL.createObjectURL(blob);
        setImageUrl(url);
      } catch {
        setImageFailed(true);
      }
    }).catch(() => {
      if (!cancelledLoad) setImageFailed(true);
    });
    return () => {
      cancelledLoad = true;
      if (url) {
        try { URL.revokeObjectURL(url); } catch { /* already gone */ }
      }
    };
  }, [api, jobId, currentId, imageAttempt]);

  const changeInk = useCallback((next: boolean) => {
    setHasInk(next);
    setError(null);
    onInkChange?.(next);
  }, [onInkChange]);

  const reset = useCallback(() => {
    padRef.current?.clear();
    setSignerName('');
    setError(null);
  }, []);

  const collect = useCallback(async (): Promise<CollectedSignature> => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) return { ok: true, signature: null };
    const nameIssue = signerNameError(signerName);
    if (nameIssue) {
      setError(done ? nameIssue : `${nameIssue} Or clear the signature to finish without one.`);
      return { ok: false, message: nameIssue };
    }
    const blob = await pad.toBlob();
    if (!blob) {
      const message = 'The signature could not be read. Clear it and sign again, or finish without one.';
      setError(message);
      return { ok: false, message };
    }
    return { ok: true, signature: { blob, signerName: signerName.trim() } };
  }, [signerName, done]);

  useImperativeHandle(ref, () => ({
    collect,
    reload: () => {
      reset();
      setSaved(false);
      void load();
    },
  }), [collect, reset, load]);

  const save = async () => {
    if (saveInFlight.current || busy) return;
    saveInFlight.current = true;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await collect();
      if (!result.ok) return;
      if (!result.signature) {
        setError('Ask the customer to sign in the box first.');
        return;
      }
      try {
        await api.uploadTechnicianSignature(jobId, result.signature);
      } catch (err) {
        setError(signatureErrorMessage(getErrorCode(err)) ?? getErrorMessage(err, 'Could not save the signature. Try again.'));
        return;
      }
      reset();
      setSaved(true);
      await load();
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const current = list?.current ?? null;
  const disabled = busy || saving;
  const canSign = !cancelled;

  return (
    <section className={box} aria-label="Signature">
      <h2 className="text-sm font-bold text-gray-900">Customer signature</h2>

      {loading && !list && <p role="status" className="mt-1 text-sm text-gray-500">Loading signature...</p>}
      {loadError && (
        <div role="alert" className="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">
          <p>{loadError}</p>
          <button type="button" className={`${secondaryButtonClass} mt-2 min-h-[44px]`} disabled={loading} onClick={() => void load()}>Try again</button>
        </div>
      )}

      {current && (
        <div className="mt-3" data-testid="current-signature">
          {imageUrl ? (
            <img src={imageUrl} alt={`Signature of ${current.signerName}`} className="max-h-40 w-full rounded-md border border-gray-200 bg-white object-contain" />
          ) : imageFailed ? (
            <p role="alert" className="text-sm text-red-800">
              The signature image could not be loaded.{' '}
              <button type="button" className="min-h-[44px] text-blue-700 underline" onClick={() => setImageAttempt((n) => n + 1)}>Try again</button>
            </p>
          ) : (
            <p role="status" className="text-sm text-gray-500">Loading image...</p>
          )}
          <p className="mt-1 text-sm text-gray-700">
            Signed by <span className="font-semibold">{current.signerName}</span> on {formatSignedAt(current.signedAt, timeZone)}
          </p>
        </div>
      )}
      {!current && !loading && !loadError && list && (
        <p className="mt-1 text-sm text-gray-500">{done ? 'No signature on this job yet.' : 'No signature yet. It is optional.'}</p>
      )}

      {cancelled && <p className="mt-3 text-sm text-gray-600">This job is cancelled, so it cannot be signed.</p>}

      {canSign && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-gray-700">
            {done ? (current ? 'Replace the signature' : 'Add a signature') : 'Sign before finishing (optional)'}
          </p>
          <SignaturePad ref={padRef} disabled={disabled} onInkChange={changeInk} label="Customer signature area" />
          <div className="mt-3">
            <label htmlFor="signer-name" className={labelClass}>Printed name</label>
            <input
              id="signer-name"
              type="text"
              className={`${inputClass} min-h-[44px]`}
              value={signerName}
              maxLength={types.SIGNER_NAME_MAX}
              disabled={disabled}
              autoComplete="off"
              onChange={(event) => { setSignerName(event.target.value); setError(null); }}
            />
          </div>
          {!done && hasInk && (
            <p className="mt-2 text-xs text-gray-600">The signature is saved when you tap Finish.</p>
          )}
          {done && (
            <button
              type="button"
              className={`${primaryButtonClass} mt-3 min-h-[44px] w-full`}
              disabled={disabled || !hasInk}
              onClick={() => void save()}
            >
              {saving ? 'Saving...' : 'Save signature'}
            </button>
          )}
          {error && <p role="alert" className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">{error}</p>}
          {saved && !error && <p role="status" className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-900 ring-1 ring-green-200">Signature saved.</p>}
        </div>
      )}
    </section>
  );
});
